<template>

    <div class="userform">

        <div class="content">

            <h1 class="title-baseSemibold title">
                <span>{{ $t('tuto_welcome_1_title') }}</span>
                <span>{{ $t('user_form_subtitle_created') }}</span>
            </h1>

            <p class="text-lMedium mt__32">{{ $t('user_form_subtitle') }}</p>

            <input-label :placeholder="$t('user_form_firstname')"
                         class="mt__16"
                         :value="firstname"
                         :autocomplete="'given-name'"
                         :error="computedErrorTextFirstname"
                         @update="updateFirstname"></input-label>

            <input-label :placeholder="$t('user_form_birthyear')"
                         :value="birthYear"
                         :type="'number'"
                         class="mt__8"
                         :maxlength="4"
                         :autocomplete="'bday-year'"
                         :error="computedErrorBirthYearText"
                         @update="updateBirthYear"></input-label>

            <p class="text-lMedium mt__40">{{ $t('referral_title_optional') }}</p>

            <input-label :placeholder="$t('referral_place_holder')"
                         class="mt__16"
                         :error="partnerCodeErrorText"
                         @update="updatePartnerCode"
                         @pressEnter="sendUserForm"></input-label>

            <button class="cta-medium-secondary mt__40" :disabled="isCtaDisabled" @click="sendUserForm">
                <lottie-loader :size="24" v-if="requesting"></lottie-loader>
                <span v-else>{{ $t('Lets_get_started') }}</span>
            </button>
        </div>
    </div>

</template>

<script>

import Vuex from 'vuex'
import Api from '@/api'

import Storage from '@/helpers/storage'

import Tag from '@/modules/tag'
import UserAgent from '@/modules/userAgent'
import UserFlow from '@/modules/userFlow'

import InputLabel from '@/components/inputs/InputLabel'
import LottieLoader from '@/components/lottie/LottieLoader'

export default {

    name: 'user-form',

    components: {
        InputLabel,
        LottieLoader
    },

    watch: {
        firstname() {
            this.isErrorFirstname = false
        },
        birthYear() {
            this.isErrorBirthyear = false
        }
    },

    computed: {

        ...Vuex.mapGetters(['user']),

        computedValidFirstname() {
            return this.firstname.length > 1
        },

        computedValidBirthYear() {
            const year = parseInt(this.birthYear, 10)
            const old = new Date().getFullYear() - year
            return !!(old && old > 14 && old < 101)
        },

        isValidForm() {
            return this.computedValidFirstname && this.computedValidBirthYear
        },

        computedErrorTextFirstname() {
            if (this.isErrorFirstname) {
                return this.errorFirstnameArray
                    ? this.$tc('user_form_firstname_invalid', this.errorFirstnameArray.length, {'invalid_characters': this.beautifull(this.errorFirstnameArray)})
                    : this.$t('user_form_firstname_error')
            } else {
                return ''
            }
        },

        computedErrorBirthYearText() {
            return this.isErrorBirthyear ? this.$t('user_form_birthyear_error') : ''
        },

        isCtaDisabled() {
            return !this.isValidForm || this.requesting
        }

    },

    data() {
        return {
            firstname: '',
            birthYear: '',
            errorFirstnameArray: null,
            errorBirthYear: null,
            partnerCode: '',
            requesting: false,
            partnerCodeErrorText: '',
            isErrorFirstname: false,
            isErrorBirthyear: false
        }
    },

    created() {
        UserFlow.setPage(this, 'user_form')
    },

    mounted() {
        this.partnerCode = Storage.getItem(Storage.KEYS.PARTNER_CODE) || ''
    },

    methods: {

        ...Vuex.mapActions([
            'setUser'
        ]),

        updateFirstname(value) {
            this.firstname = value
        },

        updateBirthYear(value) {
            this.birthYear = value
        },

        updatePartnerCode(partnerCode) {
            this.partnerCodeErrorText = false
            this.partnerCode = partnerCode
        },

        beautifull(array) {
            return array.map(char => `"${char}"`)
                .toString()
                .replace(',', ', ')
        },

        goFetch() {
            this.routerPush('/fetch')
            Storage.removeItem(Storage.KEYS.PARTNER_CODE)
        },

        parseError(error) {

            console.error('update user error: ', error)
            const obj = {}

            //sometimes it's an array, sometimes it's a single object with name = "partnerCode"
            if (error.data && error.data.name === 'partnerCode') {
                obj['partnerCode'] = error.data.error
            } else {
                error.data.forEach(data => {
                    obj[data.name] = data.error
                })
            }

            this.errorFirstnameArray = obj.firstname ? obj.firstname[0].characters : null
            this.errorBirthYear = obj.birthYear ? obj.birthYear[0].error : null
            const isPartnerCodeError = this.partnerCodeErrorText = obj.partnerCode ? obj.partnerCode[0].error : ''

            if (this.errorFirstnameArray && this.errorFirstnameArray.length) {
                this.isErrorFirstname = true
            }
            if (this.errorBirthYear) {
                this.isErrorBirthyear = true
            }

            switch (isPartnerCodeError) {
                case 'unknown_code':
                    this.partnerCodeErrorText = this.$t('referral_error')
                    break
                case 'already_referred':
                    this.partnerCodeErrorText = this.$t('login_email_already_associated')
                    break
                // should not happen
                // case 'cycling_referral':
                // case 'self_add':
                default:
                    this.partnerCodeErrorText = this.$t('cleaning_list_technical_error')
                    break
            }
        },

        sendUserForm() {
            const provider = this.user && this.user.accounts && this.user.accounts[0] ? this.user.accounts[0].provider : ''
            Tag.clickAmplitude(this, 'next', provider)
            this.requesting = true
            Api.user.updateUser({
                firstname: this.firstname,
                birthYear: this.birthYear,
                partnerCode: this.partnerCode,
                device: UserAgent.getDevice()
            })
                .then(async user => {
                    this.requesting = false
                    await this.setUser(user)
                    this.goFetch()
                })
                .catch(error => {
                    this.requesting = false
                    this.parseError(error)
                })
        }
    }
}
</script>

<style lang="scss" scoped>

.userform {
    display: flex;
    flex-direction: column;
    background-image: url('/img/background/fetch-mobile.svg');
    background-color: $aquamarine;
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: cover;
    @media screen and (min-width: $above-tablet-length) {
        background-image: url('/img/background/fetch-desktop.svg');
        align-items: center;
    }

    .content {
        color: $white;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        text-align: center;
        padding: 16px;
        max-width: 432px;
        justify-content: start;
        @media screen and (min-width: $above-tablet-length) {
            justify-content: center;
        }

        .title {
            display: flex;
            flex-direction: column;
        }

        label {
            background-color: $white;
        }
    }
}

</style>
